import React, { useCallback, useEffect, useState } from "react";
import { Drawer, Table, Tabs, Tag, Tooltip } from "antd";
import { useDispatch } from "react-redux";
import { Button, Icon } from "../../../components/Component";
import dayjs from "dayjs";
import { uploadApis } from "../../../api/Uploads";
import { machineryApis } from "../../../api/MachineryNorms";
import { setExportStatus } from "../../../store/slices/misc/highlightSlice";

// Combined machinery norms history: IMPORTS (excel upload,
// reportType=machinery-norms-upload) and EXPORTS (async xlsx generation) in one
// drawer, one tab each. Read-only — the upload/export modals own the lifecycle;
// this drawer answers "what happened".
//
// Pagination: both tabs fetch the newest FETCH_CAP records once and let antd
// Table paginate them client-side. Machinery uploads/exports are low-volume
// org-level batches, so the cap is generous; deep history still lives on the
// full /pms/bulk-upload-list & exports pages.
const PAGE_SIZE = 10;
const FETCH_CAP = 100;

type HistoryTab = "imports" | "exports";

const tablePagination = {
  pageSize: PAGE_SIZE,
  size: "small" as const,
  showSizeChanger: false,
  hideOnSinglePage: true,
  showTotal: (total: number) => `${total} record${total === 1 ? "" : "s"}`,
};

// ---- imports status (upload lifecycle) ----
const importStatusView = (record: any): { label: string; color: string } => {
  const { uploadStatus, status } = record || {};
  if (uploadStatus === "Preview") {
    if (["Requested", "Validating"].includes(status))
      return { label: "Validating", color: "processing" };
    if (status === "Processed")
      return { label: "Validated — not yet saved", color: "warning" };
    if (["Error", "Failed"].includes(status))
      return { label: "Validation failed", color: "error" };
  }
  if (uploadStatus === "Submit") {
    if (["Requested", "Processing", "Validating"].includes(status))
      return { label: "Saving", color: "processing" };
    if (status === "Processed") return { label: "Imported", color: "success" };
    if (["Error", "Failed"].includes(status))
      return { label: "Save failed", color: "error" };
  }
  return { label: status || "Unknown", color: "default" };
};

// ---- exports status (generation lifecycle) ----
const exportStatusView = (status: string): { label: string; color: string } => {
  switch (status) {
    case "Requested":
      return { label: "Queued", color: "default" };
    case "Processing":
      return { label: "Generating", color: "processing" };
    case "Processed":
      return { label: "Ready", color: "success" };
    case "Failed":
      return { label: "Failed", color: "error" };
    default:
      return { label: status || "Unknown", color: "default" };
  }
};

// Plain styled anchor — NOT an antd Button (global CSS forces a blue background
// on themed buttons; same gotcha handled in MachineryNormsExportModal).
const downloadAnchorStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  color: "#1ee0ac",
  fontWeight: 500,
};

const MachineryNormsHistoryDrawer = ({
  open,
  onClose,
  defaultTab = "imports",
}: {
  open: boolean;
  onClose: () => void;
  defaultTab?: HistoryTab;
}) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState<HistoryTab>(defaultTab);

  // Viewing the Exports tab = the user has "looked" → clear the History mark.
  useEffect(() => {
    if (open && activeTab === "exports") {
      dispatch(setExportStatus("idle"));
    }
  }, [open, activeTab, dispatch]);

  const [importRows, setImportRows] = useState<any[]>([]);
  const [importLoading, setImportLoading] = useState(false);
  const [exportRows, setExportRows] = useState<any[]>([]);
  const [exportLoading, setExportLoading] = useState(false);

  const fetchImports = useCallback(async () => {
    try {
      setImportLoading(true);
      const res = await uploadApis.fetchWBSWithPagination({
        sortOrder: "DESC",
        direction: "P",
        pageSize: FETCH_CAP,
        pageDiff: 0,
        reportType: "machinery-norms-upload",
      });
      const data = res?.data || res;
      setImportRows(data?.list || []);
    } catch (error) {
      console.error("machinery upload history fetch failed:", error);
      setImportRows([]);
    } finally {
      setImportLoading(false);
    }
  }, []);

  const fetchExports = useCallback(async () => {
    try {
      setExportLoading(true);
      const res = await machineryApis.fetchExportList();
      setExportRows(res?.data?.records || []);
    } catch (error) {
      console.error("machinery export history fetch failed:", error);
      setExportRows([]);
    } finally {
      setExportLoading(false);
    }
  }, []);

  const loadTab = useCallback(
    (tab: HistoryTab) => (tab === "imports" ? fetchImports() : fetchExports()),
    [fetchImports, fetchExports]
  );

  useEffect(() => {
    if (open) {
      setActiveTab(defaultTab);
      loadTab(defaultTab);
    }
  }, [open, defaultTab, loadTab]);

  const onTabChange = (key: string) => {
    const tab = key as HistoryTab;
    setActiveTab(tab);
    loadTab(tab);
  };

  const importColumns: any[] = [
    { title: "Report Id", dataIndex: "reportId", key: "reportId", width: 90 },
    {
      title: "Uploaded",
      dataIndex: "requestedDate",
      key: "requestedDate",
      width: 150,
      render: (text: any) =>
        text ? dayjs(text).format("DD-MMM-YYYY HH:mm") : "-",
    },
    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
      width: 180,
      ellipsis: true,
      render: (text: any) => (
        <Tooltip title={text}>
          <span>{text || "-"}</span>
        </Tooltip>
      ),
    },
    {
      title: "Rows (ok / errors)",
      key: "rows",
      width: 140,
      render: (_: any, record: any) => (
        <span>
          {record?.totalRows ?? "-"} ({record?.successCount ?? 0} /{" "}
          {record?.failureCount ?? 0})
        </span>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 190,
      render: (_: any, record: any) => {
        const view = importStatusView(record);
        const topError =
          typeof record?.error === "string" && record?.error !== "[]"
            ? record.error
            : "";
        const tag = <Tag color={view.color}>{view.label}</Tag>;
        return topError ? <Tooltip title={topError}>{tag}</Tooltip> : tag;
      },
    },
  ];

  const exportColumns: any[] = [
    { title: "Report Id", dataIndex: "reportId", key: "reportId", width: 90 },
    {
      title: "Requested",
      dataIndex: "requestedDate",
      key: "requestedDate",
      width: 150,
      render: (text: any) =>
        text ? dayjs(text).format("DD-MMM-YYYY HH:mm") : "-",
    },
    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
      width: 170,
      ellipsis: true,
      render: (text: any) => (
        <Tooltip title={text}>
          <span>{text || "-"}</span>
        </Tooltip>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 130,
      render: (_: any, record: any) => {
        const view = exportStatusView(record?.status);
        const tag = <Tag color={view.color}>{view.label}</Tag>;
        return record?.status === "Failed" && record?.error ? (
          <Tooltip title={record.error}>{tag}</Tooltip>
        ) : (
          tag
        );
      },
    },
    {
      title: "File",
      key: "download",
      width: 120,
      render: (_: any, record: any) =>
        record?.status === "Processed" && record?.filePath ? (
          <a
            href={record.filePath}
            target="_blank"
            rel="noreferrer"
            download
            title={record?.fileName || "Download export"}
            style={downloadAnchorStyle}
          >
            <Icon name="download" />
            <span>Download</span>
          </a>
        ) : (
          <span className="text-soft">—</span>
        ),
    },
  ];

  const refreshActive = () => loadTab(activeTab);

  return (
    <Drawer
      title="Machinery Norms History"
      placement="right"
      width={760}
      open={open}
      onClose={onClose}
      extra={
        <Button size="sm" color="light" outline onClick={refreshActive}>
          <Icon name="redo" />
        </Button>
      }
    >
      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        items={[
          {
            key: "imports",
            label: "Imports",
            children: (
              <Table
                columns={importColumns}
                dataSource={importRows.map((row, index) => ({
                  ...row,
                  key: row?.reportId ?? index,
                }))}
                size="small"
                loading={importLoading}
                pagination={tablePagination}
                scroll={{ x: 700 }}
              />
            ),
          },
          {
            key: "exports",
            label: "Exports",
            children: (
              <Table
                columns={exportColumns}
                dataSource={exportRows.map((row, index) => ({
                  ...row,
                  key: row?.reportId ?? index,
                }))}
                size="small"
                loading={exportLoading}
                pagination={tablePagination}
                scroll={{ x: 640 }}
              />
            ),
          },
        ]}
      />
    </Drawer>
  );
};

export default MachineryNormsHistoryDrawer;
